import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, message, Modal, Result, Space, Spin } from 'antd';
import { AimOutlined, ArrowLeftOutlined, CheckOutlined, CloseOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import AoiDetailMap from './components/AoiDetailMap';
import AoiDetailInfoPanel from './components/AoiDetailInfoPanel';
import AoiAttributeEditPanel, { AoiAttributeDraft, initDraftFromDetail } from './components/AoiAttributeEditPanel';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import {
    AOI_DETAIL_COLORS,
    DEFAULT_LAYER_VISIBILITY,
    LayerVisibility,
    MAX_LIST_SIZE,
    MapProvider,
    PickTarget,
    TempPickedPoint,
} from './constants';
import { serializeDeliveryTime, validateDeliveryTime } from './utils/deliveryTime';
import { isPointInPolygon } from './utils/geometry';
import {
    GET_AOI_ATTRIBUTE_DETAIL,
    GetAoiAttributeDetailResponse,
} from './graphql/queries/getAoiAttributeDetail';
import {
    BizAoiAttributeUpsertInput,
    UPSERT_AOI_ATTRIBUTE,
    UpsertAoiAttributeResponse,
} from './graphql/mutations/upsertAoiAttribute';

type Mode = 'view' | 'edit';

/**
 * 保存请求前端超时 (ms)。
 *
 * <p>15s 是 BFF 端 dubbo timeout (10s) + 5s 留给 BFF 自己处理 (校验 / 序列化 / 标准化地址解析等)。
 * 一旦超时, withTimeout 抛错, UI 退出 loading 状态让用户重试。
 * 注: 这只是 UX 兜底, 不真正取消 mutation; Apollo 内部 request 还可能在飞, 但至少前端不再卡。
 */
const SAVE_TIMEOUT_MS = 15_000;

/**
 * Promise 超时包装 - 给定 ms 内未 resolve / reject 就以 message 抛 Error。
 *
 * <p>用法: 让前端在网络 / 后端 hang 住时不会一直转圈, 给用户一个明确的 "超时" 反馈。
 * 注: 这不会真正取消底层请求 (Apollo Client v3 取消需要 AbortController, 嫌麻烦先这样); 业务上够用。
 */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(message)), ms);
        promise.then(
            (v) => { clearTimeout(timer); resolve(v); },
            (e) => { clearTimeout(timer); reject(e); },
        );
    });
}

/**
 * AOI 生产管理 - 详情页 (查看 + 编辑合一)。
 *
 * <p>view 模式:渲染 {@link AoiDetailInfoPanel}, 信息只读, 顶部有「编辑」按钮。
 * <p>edit 模式:渲染 {@link AoiAttributeEditPanel}, 字段可改, 顶部有「保存 / 取消」按钮。
 *
 * <p>地图永远显示, 编辑模式下右上区域出现「拾取状态条」, 接管地图点击事件;
 * 同一时刻只允许拾取一种点位 (进门 OR 停车), 防止地图 click 意图不明确。
 *
 * <p>留 TODO:
 * <ul>
 *   <li>左侧地图的面绘制 / 重新画 polygon: 需要后端新接口更新 biz_aoi_gis;</li>
 *   <li>关联地址增删: 需要后端新接口;</li>
 *   <li>新增整个 AOI: 需要后端 createBizAoi + GIS 注入接口;</li>
 *   <li>已保存进门 / 停车点的拖拽微调 (现在只支持删除 + 重新拾取)。</li>
 * </ul>
 */
const AoiPlatformDetailPage: React.FC = () => {
    const { aoiId: aoiIdParam } = useParams<{ aoiId: string }>();
    const [searchParams] = useSearchParams();
    const zipcode = searchParams.get('zipcode') ?? '';
    const navigate = useNavigate();

    const aoiId = aoiIdParam ? Number(aoiIdParam) : NaN;

    const [mode, setMode] = useState<Mode>('view');
    const [mapProvider, setMapProvider] = useState<MapProvider>('google');
    const [layerVisibility, setLayerVisibility] = useState<LayerVisibility>(DEFAULT_LAYER_VISIBILITY);
    // 地址级点位"按地址查看": 选中某关联地址 → 地图高亮它自己的进门/停车点(其余淡化), null=看全部
    const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);

    // 编辑草稿: 提到父组件来, 跟拾取状态在同一层级, 便于把 tempPoint 拼回 draft
    const [draft, setDraft] = useState<AoiAttributeDraft | null>(null);

    // 拾取状态: pickTarget 决定地图 click 进哪条列表, tempPoint 是用户已点出但未确认的位置
    const [pickTarget, setPickTarget] = useState<PickTarget>(null);
    const [tempPoint, setTempPoint] = useState<TempPickedPoint | null>(null);

    const skip = !aoiId || !zipcode;
    const { data, loading, error, refetch } = useQuery<GetAoiAttributeDetailResponse>(
        GET_AOI_ATTRIBUTE_DETAIL,
        {
            variables: skip ? undefined : { aoiId, zipcode },
            skip,
            fetchPolicy: 'cache-and-network',
        },
    );

    const detail = data?.getAoiAttributeDetail;

    /*
     * draft 重置时机: 仅在 view 模式下跟 detail 同步, 避免编辑过程中后台 refetch (cache-and-network)
     * 把用户未保存的改动覆盖掉。edit → view 的切换也会进这个分支, 起到「取消时撤销改动」的作用。
     */
    useEffect(() => {
        if (mode === 'view' && detail) {
            setDraft(initDraftFromDetail(detail));
            setPickTarget(null);
            setTempPoint(null);
        }
    }, [mode, detail]);

    // 切换 AOI / 进编辑态: 清掉地址级点位的选中高亮
    useEffect(() => {
        setSelectedAddressId(null);
    }, [aoiId, mode]);

    const [upsertMutation] = useMutation<UpsertAoiAttributeResponse>(UPSERT_AOI_ATTRIBUTE);
    // 我们自己管控保存 loading 状态, 不用 Apollo 的 `loading` - 后端 hang 时它永远不变 false, 按钮会一直转圈。
    // 我们的 saving 在 handleSave 的 finally 里强制 setSaving(false), 即使 mutation 还在飞, 至少 UI 能恢复让用户重试。
    const [saving, setSaving] = useState(false);

    // Esc 快捷键退出拾取模式 (跟「取消」按钮等价); 只在拾取中挂监听, 避免干扰其他键盘交互。
    // 注: 必须放在所有 early return 之前, 否则 hooks 顺序会乱。
    useEffect(() => {
        if (!pickTarget) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setPickTarget(null);
                setTempPoint(null);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [pickTarget]);

    if (skip) {
        return (
            <div className="p-6">
                <Result
                    status="warning"
                    title="参数缺失"
                    subTitle="aoiId 或 zipcode 缺失, 无法加载详情。"
                    extra={<Button onClick={() => navigate('/central/map-tool/aoi-platform')}>返回列表</Button>}
                />
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-6">
                <Result
                    status="error"
                    title="加载失败"
                    subTitle={error.message}
                    extra={<Button onClick={() => navigate('/central/map-tool/aoi-platform')}>返回列表</Button>}
                />
            </div>
        );
    }

    /* ====================== 编辑保存 ====================== */
    const handleSave = async () => {
        if (!detail || !draft) return;

        // 列表上限 (跟后端 50 校验保持一致, 前端先拦一道避免冗余请求)
        if (draft.entryPoints.length > MAX_LIST_SIZE) {
            message.error(`进门点数量超过上限 ${MAX_LIST_SIZE}`);
            return;
        }
        if (draft.parkingPoints.length > MAX_LIST_SIZE) {
            message.error(`停车点数量超过上限 ${MAX_LIST_SIZE}`);
            return;
        }

        // 校验进门点 / 停车点经纬度必填
        const points = [...draft.entryPoints, ...draft.parkingPoints];
        for (const p of points) {
            if (p.lng == null || p.lat == null) {
                message.error('进门点 / 停车点必须填经度和纬度');
                return;
            }
        }

        // 校验配送时间 (格式 / 数量上限 / other.name 非空)
        const deliveryCheck = validateDeliveryTime(draft.deliveryTime);
        if (!deliveryCheck.ok) {
            message.error(deliveryCheck.message ?? '配送时间校验失败');
            return;
        }

        // 校验门禁 code: 类型 / 码值必填 + 同 类型 + 码值 不重复 (服务端 uid 也会撞)
        if (draft.accessCodes.length > MAX_LIST_SIZE) {
            message.error(`门禁 code 数量超过上限 ${MAX_LIST_SIZE}`);
            return;
        }
        const seenCodes = new Set<string>();
        for (let i = 0; i < draft.accessCodes.length; i++) {
            const c = draft.accessCodes[i];
            if (!c.codeType.trim()) {
                message.error(`门禁 code 第 ${i + 1} 条未选择类型`);
                return;
            }
            if (!c.codeValue.trim()) {
                message.error(`门禁 code 第 ${i + 1} 条 code 值不能为空`);
                return;
            }
            const key = `${c.codeType.trim()}:${c.codeValue.trim()}`;
            if (seenCodes.has(key)) {
                message.error(`门禁 code 第 ${i + 1} 条与已有条目重复 (类型 + code 相同)`);
                return;
            }
            seenCodes.add(key);
        }

        const upsertInput: BizAoiAttributeUpsertInput = {
            aoiId: detail.aoiId,
            zipcode: detail.zipcode ?? zipcode,
            entryPoints: draft.entryPoints.map((p) => ({ lng: p.lng!, lat: p.lat!, remark: p.remark })),
            parkingPoints: draft.parkingPoints.map((p) => ({ lng: p.lng!, lat: p.lat!, remark: p.remark })),
            // 结构化对象序列化成 JSON 字符串给后端 (全空时存 null)
            accessCodes: draft.accessCodes.map((c) => ({
                codeType: c.codeType.trim(),
                codeValue: c.codeValue.trim(),
                available: c.available,
                remark: c.remark?.trim() || null,
            })),
            deliveryTime: serializeDeliveryTime(draft.deliveryTime),
            typeCode: draft.typeCode,
            aoiSourceId: detail.attribute?.aoiSourceId ?? null,
        };

        setSaving(true);
        try {
            // 衍生属性 upsert (点位 / 门禁 / 开门时间 / 类型 一次写完; 前端 15s 超时, 大于 BFF 端 dubbo 10s 上限, 留 5s 给 BFF 自己的处理时间)
            const res = await withTimeout(
                upsertMutation({ variables: { input: upsertInput } }),
                SAVE_TIMEOUT_MS,
                '保存请求超时, 请稍后重试',
            );
            const r = res.data?.upsertAoiAttribute;

            message.success(r?.created ? '新增成功' : '更新成功');
            setMode('view');
            await refetch();
        } catch (e) {
            // 不把 dubbo / GraphQL 系统错误原文丢给用户; 业务级 message 已经在 BFF/前端校验里给过, 这里兜底用通用文案
            console.error('handleSave failed:', e);
            const msg = (e as Error).message;
            if (msg.includes('超时')) {
                // withTimeout 抛的, 文案已经够友好直接用
                message.error(msg);
            } else {
                message.error('保存失败, 请稍后重试');
            }
        } finally {
            // 不管成功失败, 强制把 button loading 收掉, 让用户能再点一次。
            // 即使 Apollo 内部 mutation 还在飞 (网络 hang), UI 也已经恢复可交互。
            setSaving(false);
        }
    };

    /* ====================== 拾取 (point picking) ====================== */
    const handleStartPick = (target: 'entryPoints' | 'parkingPoints') => {
        // 切换或重新进入拾取 - 抛弃之前的 tempPoint
        if (pickTarget === target) {
            // 再次点同一个按钮 = 取消拾取
            setPickTarget(null);
            setTempPoint(null);
        } else {
            setPickTarget(target);
            setTempPoint(null);
        }
    };
    // 选中某地址查看其点位: 同步打开地址级点位图层(高亮点归属该图层)
    const handleSelectAddress = (addressCoreId: number | null) => {
        setSelectedAddressId(addressCoreId);
        if (addressCoreId != null && !layerVisibility.showAddressPoints) {
            setLayerVisibility({ ...layerVisibility, showAddressPoints: true });
        }
    };

    const handleMapPickClick = (lng: number, lat: number) => {
        if (!pickTarget) return;
        setTempPoint({ lng, lat });
        // 软提示: 点击位置在当前 AOI 多边形外 (不阻止, 用户可能确实想标边界外的进门 / 停车点)
        if (detail?.selfGeometry?.geometryGeoJson) {
            if (!isPointInPolygon(lng, lat, detail.selfGeometry.geometryGeoJson)) {
                message.warning('该位置在当前 AOI 多边形外, 请确认是否符合预期');
            }
        }
    };
    const handleConfirmPick = () => {
        if (!pickTarget || !tempPoint || !draft) return;
        // 防御性上限检查 (UI 按钮已经在到达上限时 disabled, 这里再兜一道)
        if (draft[pickTarget].length >= MAX_LIST_SIZE) {
            message.error(`${pickTarget === 'entryPoints' ? '进门点' : '停车点'}数量已达上限 ${MAX_LIST_SIZE}`);
            setPickTarget(null);
            setTempPoint(null);
            return;
        }
        const list = [...draft[pickTarget], { lng: tempPoint.lng, lat: tempPoint.lat, remark: null }];
        setDraft({ ...draft, [pickTarget]: list });
        setPickTarget(null);
        setTempPoint(null);
    };
    const handleCancelPick = () => {
        setPickTarget(null);
        setTempPoint(null);
    };

    /**
     * 已保存的进门 / 停车点拖拽微调回写到 draft 对应位置。
     * 跟 handleConfirmPick 不同, 这里是改一条已有的, 不增减数量。
     */
    const handlePointDrag = (
        target: 'entryPoints' | 'parkingPoints',
        idx: number,
        lng: number,
        lat: number,
    ) => {
        if (!draft) return;
        const list = draft[target].map((p, i) => (i === idx ? { ...p, lng, lat } : p));
        setDraft({ ...draft, [target]: list });
    };

    /* ====================== 渲染 ====================== */
    return (
        <div className="flex flex-col h-full">
            {/* 顶部标题栏 + 操作按钮; 左侧不留 padding 让"返回列表"按钮跟下方地图左边对齐 */}
            <div className="flex items-center justify-between pr-6 py-3 border-b bg-white">
                <div className="flex items-center gap-3">
                    {/*
                     * 返回列表 = 次级导航 (vs 编辑 = 主操作)。
                     * 配色用浅紫色填底 + 深紫文字, 跟整个 AOI 平台的紫色主题对齐 (selfStroke = purple-700),
                     * 但比 type="primary" 编辑按钮的实紫色弱一档, 主次分明、不抢戏。
                     */}
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            if (mode === 'edit') {
                                Modal.confirm({
                                    title: '当前在编辑中, 确认放弃?',
                                    onOk: () => navigate('/central/map-tool/aoi-platform'),
                                });
                            } else {
                                navigate('/central/map-tool/aoi-platform');
                            }
                        }}
                        style={{
                            background: '#f5f3ff',     // purple-50
                            borderColor: '#ddd6fe',    // purple-200
                            color: '#6d28d9',          // purple-700 (= AOI_DETAIL_COLORS.selfStroke)
                            fontWeight: 500,
                        }}
                    >返回列表</Button>
                    <h4 className="text-lg font-bold m-0">
                        {mode === 'edit' ? '编辑 AOI' : 'AOI 详情'} - {detail?.displayName ?? `#${aoiId}`}
                    </h4>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                        AOI ID: {aoiId} · 邮编: {zipcode}
                    </span>
                    {detail && mode === 'view' && (
                        <Space>
                            <HasResource resourceCodes={[ResourceCode.aoiPlatform.UPSERT]}>
                                <Button type="primary" icon={<EditOutlined />} onClick={() => setMode('edit')}>
                                    编辑
                                </Button>
                            </HasResource>
                        </Space>
                    )}
                    {detail && mode === 'edit' && (
                        <Space>
                            <Button onClick={() => setMode('view')}>取消</Button>
                            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                                保存
                            </Button>
                        </Space>
                    )}
                </div>
            </div>

            {/* 主体 */}
            <div className="flex-1 flex overflow-hidden">
                {loading && !detail ? (
                    <div className="w-full flex items-center justify-center"><Spin tip="加载中..." /></div>
                ) : detail && draft ? (
                    <>
                        <div className="flex-1 relative" style={{ minWidth: 0 }}>
                            <AoiDetailMap
                                detail={detail}
                                provider={mapProvider}
                                layerVisibility={layerVisibility}
                                onProviderChange={setMapProvider}
                                // edit 模式下走 draft, 让地图实时反映新增 / 删除; view 模式走 detail
                                entryPoints={mode === 'edit' ? draft.entryPoints : (detail.attribute?.entryPoints ?? [])}
                                parkingPoints={mode === 'edit' ? draft.parkingPoints : (detail.attribute?.parkingPoints ?? [])}
                                pickTarget={mode === 'edit' ? pickTarget : null}
                                tempPoint={tempPoint}
                                onPickClick={handleMapPickClick}
                                // edit 模式 + 非拾取中 → 已保存点位可拖动微调
                                editable={mode === 'edit'}
                                onPointDrag={handlePointDrag}
                                selectedAddressId={mode === 'view' ? selectedAddressId : null}
                            />
                            {/* 拾取状态条 (悬浮在地图顶部, 仅 edit 模式 + 已进入拾取时显示) */}
                            {mode === 'edit' && pickTarget && (
                                <PickStatusBanner
                                    pickTarget={pickTarget}
                                    tempPoint={tempPoint}
                                    onConfirm={handleConfirmPick}
                                    onCancel={handleCancelPick}
                                />
                            )}
                        </div>
                        <div className="w-[420px] bg-gray-50 border-l p-3 overflow-hidden">
                            {mode === 'view' ? (
                                <AoiDetailInfoPanel
                                    detail={detail}
                                    layerVisibility={layerVisibility}
                                    onLayerVisibilityChange={setLayerVisibility}
                                    selectedAddressId={selectedAddressId}
                                    onSelectAddress={handleSelectAddress}
                                />
                            ) : (
                                <AoiAttributeEditPanel
                                    detail={detail}
                                    draft={draft}
                                    onDraftChange={setDraft}
                                    pickTarget={pickTarget}
                                    onStartPick={handleStartPick}
                                    onRefetchDetail={() => { refetch().catch(() => { /* refetch 错误已被全局拦截 */ }); }}
                                />
                            )}
                        </div>
                    </>
                ) : (
                    <div className="w-full flex items-center justify-center">
                        <Result status="warning" title="没有数据" />
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * 拾取状态条 - 悬浮在地图顶部居中位置, 给用户清晰的「下一步该干什么」提示。
 *
 * <p>三个状态:
 * <ul>
 *   <li>刚进入拾取 (tempPoint == null): 提示「请在地图上点击位置」+ 取消按钮</li>
 *   <li>已点出位置 (tempPoint != null): 显示坐标 + 「确定 / 取消」按钮 + 提示可拖动微调</li>
 * </ul>
 */
const PickStatusBanner: React.FC<{
    pickTarget: 'entryPoints' | 'parkingPoints';
    tempPoint: TempPickedPoint | null;
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ pickTarget, tempPoint, onConfirm, onCancel }) => {
    const isEntry = pickTarget === 'entryPoints';
    const dotColor = isEntry ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint;
    const targetLabel = isEntry ? '进门点' : '停车点';

    return (
        <div
            className="absolute top-4 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-xl flex items-center gap-4 px-6 py-3 border border-gray-200"
            style={{ zIndex: 1100, minWidth: 520 }}
        >
            <AimOutlined style={{ color: dotColor, fontSize: 22 }} />
            <span className="inline-block w-3.5 h-3.5 rounded-full" style={{ background: dotColor }} />
            <span className="text-base font-medium">
                {tempPoint
                    ? <>已选 <strong>{targetLabel}</strong>: {tempPoint.lng.toFixed(7)}, {tempPoint.lat.toFixed(7)}</>
                    : <>请在地图上点击选择 <strong>{targetLabel}</strong> 位置</>
                }
            </span>
            <div className="flex-1" />
            {tempPoint && (
                <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={onConfirm}
                >确定</Button>
            )}
            <Button
                icon={<CloseOutlined />}
                onClick={onCancel}
            >取消</Button>
        </div>
    );
};

export default AoiPlatformDetailPage;
